/**
 * hazardStep.ts
 * Nova-vs-spike-hazard collision resolution for one fixed physics step.
 *
 * The Tiled `Hazards` layer is parsed into spike AABBs by `TileMapLoader`.
 * Those rectangles are carried onto `LevelRuntime.hazards` so the engine can
 * see them — before this module existed they were parsed into `LevelData`
 * and then dropped, leaving spikes purely decorative in 13 of 22 levels.
 *
 * Architecture boundary: NO React, NO async, NO rendering.
 *
 * @module hazardStep
 */

import { aabbOverlaps } from './collision';
import { applyNovaContactDamage } from './novaDamage';
import { gameState } from './gameState';
import type { NovaRuntime } from './gameState';
import type { AABB } from '../types/entity';

/**
 * Resolve Nova's overlap with the level's spike hazards for this tick.
 *
 * Damage, i-frames, weapon loss, and knockback are delegated to
 * {@link applyNovaContactDamage} so spikes and enemies stay consistent.
 *
 * @param nova       - Nova runtime state, mutated in place on contact.
 * @param novaHitbox - Nova's combat hitbox for this tick, already adjusted
 *                     for crouch by the caller.
 * @returns True if a spike landed a hit this tick.
 */
export function stepHazards(nova: NovaRuntime, novaHitbox: AABB): boolean {
  const hazards = gameState.level.hazards;
  if (hazards === undefined || hazards.length === 0) return false;

  // DECISION: Stop at the first overlapping spike. Damage is capped at one
  // point per tick by the i-frame guard anyway, so continuing the scan would
  // only burn cycles across a long spike run.
  for (let i = 0; i < hazards.length; i++) {
    const hazard = hazards[i];
    if (!aabbOverlaps(novaHitbox, hazard)) continue;
    return applyNovaContactDamage(nova, hazard.x + hazard.width / 2);
  }
  return false;
}
